import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  User,
  Users,
  Shield,
  Calculator,
  KanbanSquare,
  Zap,
  Settings as SettingsIcon,
  Puzzle,
  FileText,
  Layers,
  Package,
  MessageSquare,
  Phone,
  PhoneCall,
  Truck,
  CalendarClock,
  CreditCard,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useRole } from '../../context/RoleContext';

// `ownerOnly` items are filtered out of the nav for non-owners. This is a
// UX affordance — actual authorization is enforced by <RoleGuard> on the
// /billing route itself.
const navigation = [
  { name: 'Company Profile', href: '/settings/company', icon: Building2 },
  { name: 'My Account', href: '/settings/account', icon: User },
  { name: 'Team Members', href: '/settings/team', icon: Users },
  { name: 'Roles & Permissions', href: '/settings/roles', icon: Shield },
  { name: 'Pipeline Stages', href: '/settings/pipelines', icon: KanbanSquare },
  // Pricing & Services grouped together
  { name: 'Calculator Settings', href: '/settings/pricing', icon: Calculator },
  { name: 'Service Templates', href: '/settings/service-templates', icon: FileText },
  { name: 'Service Programs', href: '/settings/service-programs', icon: Layers },
  { name: 'Catalog Items', href: '/settings/catalog-items', icon: Package },
  { name: 'Scheduling Defaults', href: '/settings/scheduling-defaults', icon: CalendarClock },
  // Messaging
  { name: 'Visit Messaging', href: '/settings/visit-messaging', icon: MessageSquare },
  { name: 'Communication',   href: '/settings/communication',   icon: Phone },
  { name: 'Business Number',  href: '/settings/business-number', icon: PhoneCall },
  { name: 'Dispatch',        href: '/settings/dispatch',        icon: Truck },
  // Automation & Config
  { name: 'Automations', href: '/settings/automations', icon: Zap },
  { name: 'App Configuration', href: '/settings/config', icon: SettingsIcon },
  { name: 'Integrations', href: '/settings/integrations', icon: Puzzle },
  // Billing (owner-only)
  { name: 'Billing & Plan', href: '/billing', icon: CreditCard, ownerOnly: true },
];

/**
 * Compact mobile section picker — rendered only below the lg breakpoint.
 * Native <select> was chosen over a scrollable pill strip because: (a) 12
 * options, (b) single-value semantics, (c) ~0 layout cost, (d) works as a
 * native system sheet on iOS/Android which is a familiar pattern for admins.
 */
const SettingsNavMobile = ({ items }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const current =
    items.find((i) => location.pathname.startsWith(i.href))?.href ||
    items[0].href;

  return (
    <div className="lg:hidden px-4 pt-3 pb-3 bg-white border-b border-gray-200 sticky top-0 z-10">
      <label htmlFor="settings-section-picker" className="sr-only">
        Settings section
      </label>
      <select
        id="settings-section-picker"
        value={current}
        onChange={(e) => navigate(e.target.value)}
        className="w-full h-11 px-3 rounded-md border border-gray-200 bg-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-secondary"
        data-testid="settings-mobile-section-picker"
      >
        {items.map((item) => (
          <option key={item.href} value={item.href}>
            {item.name}
          </option>
        ))}
      </select>
    </div>
  );
};

const SettingsNav = () => {
  const { role } = useRole() || {};
  const isOwner = role === 'owner';
  const items = navigation.filter((it) => !it.ownerOnly || isOwner);
  return (
    <>
      {/* Mobile picker — visible only on < lg */}
      <SettingsNavMobile items={items} />

      {/* Desktop sidebar — unchanged behavior on lg+ */}
      <aside className="hidden lg:block w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-80px)]">
        <nav className="p-4 space-y-1">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3">
            Settings
          </div>
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                data-testid={item.ownerOnly ? `settings-nav-${item.href.replace(/[/]/g, '-').slice(1)}` : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  )
                }
              >
                <Icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default SettingsNav;
